import { useState, useCallback, useEffect, useMemo } from 'react';
import { RoutingProfile } from '../../../../models/RoutingProfiles';
import { AgentAllocation } from '../../../../models/AgentsAllocation';

type ProfileType = 'current' | 'default';

const getAgentProfile = (agent: AgentAllocation, profileType: ProfileType): string | undefined => {
  return profileType === 'default' ? agent.defaultRoutingProfile : agent.routingProfile;
};

export const useRoutingProfileModal = (
  selectedAgents: AgentAllocation[],
  allRoutingProfiles: RoutingProfile[],
  open: boolean,
  profileType: ProfileType = 'current'
): {
  routingProfiles: RoutingProfile[];
  selectedRoutingProfile: string;
  setSelectedRoutingProfile: (value: string) => void;
  isRoutingProfileSameForAllAgents: boolean;
  isSelectionDisabled: boolean;
  resetState: () => void;
} => {
  const [routingProfiles, setRoutingProfiles] = useState<RoutingProfile[]>([]);
  const [selectedRoutingProfile, setSelectedRoutingProfile] = useState<string>('');

  const isRoutingProfileSameForAllAgents = useMemo(() => {
    if (selectedAgents.length === 0) return false;

    const firstProfile = getAgentProfile(selectedAgents[0], profileType);
    return selectedAgents.every(agent => getAgentProfile(agent, profileType) === firstProfile);
  }, [selectedAgents, profileType]);

  useEffect(() => {
    if (open && allRoutingProfiles.length > 0) {
      setRoutingProfiles(allRoutingProfiles);
      setSelectedRoutingProfile('');
    }
  }, [open, allRoutingProfiles]);

  const resetState = useCallback(() => {
    setRoutingProfiles([]);
    setSelectedRoutingProfile('');
  }, []);

  const isSelectionDisabled =
    selectedAgents.length === 0 || !isRoutingProfileSameForAllAgents || routingProfiles.length === 0;

  return {
    routingProfiles,
    selectedRoutingProfile,
    setSelectedRoutingProfile,
    isRoutingProfileSameForAllAgents,
    isSelectionDisabled,
    resetState
  };
};
